import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment/moment";
import { message } from "antd";
import DataParser from "../../utlits/Dataparser";
import MeetingsServices from "../../Services/MeetingsServices";
import SocketService from "../../Services/SocketService";

// A meeting the organiser is running past its end time: from that moment, and every 15 minutes after, they are
// asked whether it is still going on. "Still in progress" asks again in 15 minutes; "Mark it Complete" ends it
// now (as End Meeting). The 15 minutes are kept in this browser, so every tab (and a reload) waits the same.
const ASK_EVERY = 15 * 60 * 1000;
const SNOOZE_KEY = "planit_meeting_overrun";
const MODAL_ID = "meetingOverrunModal";
const errorText = (err, fallback) => err?.response?.data?.data || err?.response?.data?.message || fallback;
// its end as the meeting screen and the list show it: from when it started, plus its length
const endOf = (m) => moment(m.started_at || m.scheduled_at).second(0).millisecond(0).add(Number(m.planned_duration) || 30, "minutes");
const readSnoozes = () => {
  try {
    return JSON.parse(localStorage.getItem(SNOOZE_KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
};
const writeSnooze = (id, until) => {
  try {
    const all = readSnoozes();
    Object.keys(all).forEach((k) => { if (Number(all[k]) < Date.now() - 24 * 60 * 60 * 1000) delete all[k]; });
    all[id] = until;
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(all));
  } catch (e) { /* storage unavailable: this tab remembers it */ }
};
const allowed = () => {
  const modules = DataParser.AllowedCompanyModules() || [];
  const perms = DataParser.CompanyUserPermissions() || [];
  return modules.includes("Meetings") && perms.includes("meetings-list") && perms.includes("meetings-edit");
};
// the organiser's meeting past its end is asked about here (so the "Meeting in Progress" reminder leaves it)
export const asksAboutOverrun = (m) => !!(m && m.me && m.me.is_organiser) && allowed() && endOf(m).valueOf() <= Date.now();
// One meeting popup at a time: this question and the "Meeting in Progress" reminder (OpenMeetingReminder). Each
// marks itself open before it opens and announces POPUP_CLOSED once it has closed; the other waits for that.
export const meetingPopups = { reminder: false, overrun: false };
export const POPUP_CLOSED = "planitMeetingPopupClosed";

const MeetingOverrunPrompt = () => {
  const [asking, setAsking] = useState(null); // the meeting asked about
  const [busy, setBusy] = useState(false);
  const meetingsRef = useRef([]); // the user's own meetings in progress
  const askingRef = useRef(null);
  askingRef.current = asking;
  const snoozedRef = useRef({});

  const shown = () => !!document.querySelector(`#${MODAL_ID}.show`);
  // the popup waits while another one is open (a form being filled in, the "Meeting in Progress" reminder)
  const otherPopupOpen = () => !!document.querySelector(`.modal.show:not(#${MODAL_ID})`);
  const syncModal = useCallback(() => {
    if (askingRef.current && !shown() && !meetingPopups.reminder && !otherPopupOpen()) {
      meetingPopups.overrun = true;
      document.getElementById("openMeetingOverrunModal")?.click();
    }
    if (!askingRef.current && shown()) document.getElementById("closeMeetingOverrunModal")?.click();
    // marked open but it never opened (nothing to ask any more): the reminder need not wait
    const el = document.getElementById(MODAL_ID);
    if (!askingRef.current && meetingPopups.overrun && el && !shown() && el.style.display !== "block") {
      meetingPopups.overrun = false;
      window.dispatchEvent(new Event(POPUP_CLOSED));
    }
  }, []);

  const check = useCallback(() => {
    const now = Date.now();
    const snoozes = readSnoozes();
    const due = meetingsRef.current
      .filter((m) => endOf(m).valueOf() <= now && Math.max(Number(snoozes[m.id]) || 0, snoozedRef.current[m.id] || 0) <= now)
      .sort((a, b) => endOf(a).valueOf() - endOf(b).valueOf());
    const cur = askingRef.current;
    if (cur && !due.some((m) => m.id === cur.id)) setAsking(null); // answered in another tab, ended or made longer
    else if (!cur && due.length && !otherPopupOpen()) setAsking(due[0]);
    else if (cur) setAsking(due.find((m) => m.id === cur.id)); // its latest details
    syncModal();
  }, [syncModal]);

  const load = useCallback(() => {
    if (!allowed()) {
      meetingsRef.current = [];
      check();
      return;
    }
    MeetingsServices.getMeetings({ view: "in_progress", limit: 20, scope: "mine" })
      .then((res) => {
        if (!res?.Good) return;
        meetingsRef.current = (res.data || []).filter((m) => m.me && m.me.is_organiser && m.lifecycle_state === "in_progress");
        check();
      })
      .catch(() => {});
  }, [check]);

  useEffect(() => {
    load();
    SocketService.connect();
    // a meeting started, made longer or ended (here, in another tab, or by someone else)
    SocketService.on("meeting_update", load);
    SocketService.on("connect", load);
    const onStorage = (e) => { if (e.key === SNOOZE_KEY) check(); };
    window.addEventListener("storage", onStorage);
    // closed: the reminder may open now; the reminder closed: this may ask now
    const el = document.getElementById(MODAL_ID);
    const onHidden = () => {
      meetingPopups.overrun = false;
      window.dispatchEvent(new Event(POPUP_CLOSED));
    };
    if (el) el.addEventListener("hidden.bs.modal", onHidden);
    window.addEventListener(POPUP_CLOSED, check);
    window.addEventListener("permissionsUpdated", load);
    const tick = setInterval(check, 30 * 1000);
    const refresh = setInterval(load, 10 * 60 * 1000);
    return () => {
      SocketService.off("meeting_update", load);
      SocketService.off("connect", load);
      window.removeEventListener("storage", onStorage);
      if (el) el.removeEventListener("hidden.bs.modal", onHidden);
      window.removeEventListener(POPUP_CLOSED, check);
      window.removeEventListener("permissionsUpdated", load);
      clearInterval(tick);
      clearInterval(refresh);
    };
  }, [load, check]);

  useEffect(() => {
    syncModal();
  }, [asking, syncModal]);

  const stillRunning = () => {
    const m = askingRef.current;
    if (!m) return;
    const until = Date.now() + ASK_EVERY;
    snoozedRef.current[m.id] = until;
    writeSnooze(m.id, until);
    setAsking(null);
  };

  const markComplete = () => {
    const m = askingRef.current;
    if (!m || busy) return;
    setBusy(true);
    MeetingsServices.endMeeting(m.id, { ended_at: new Date().toISOString() })
      .then((res) => {
        if (res?.Good) {
          meetingsRef.current = meetingsRef.current.filter((x) => x.id !== m.id);
          setAsking(null);
          message.success("Meeting ended");
          // a repeating meeting: its next meeting's agenda was filled by itself (as End Meeting says)
          const n = res.next_meeting && res.next_meeting.carried;
          if (n) message.info(`${n === 1 ? "1 task was" : `${n} tasks were`} added to the next meeting on ${moment(res.next_meeting.scheduled_at).format("D MMMM")}`);
        } else message.warning(res?.data || "Could not end the meeting.");
      })
      .catch((err) => message.error(errorText(err, "Could not end the meeting.")))
      .finally(() => {
        setBusy(false);
        load();
      });
  };

  const end = asking ? endOf(asking) : null;
  const over = end ? Math.max(0, moment().diff(end, "minutes")) : 0;
  const overText = over < 1 ? "just now" : over < 60 ? `${over} min ago` : `${Math.floor(over / 60)} hr${over % 60 ? ` ${over % 60} min` : ""} ago`;

  // rendered on the page body, above the meeting popup too (as the "Meeting in Progress" reminder)
  return ReactDOM.createPortal(
    <>
      <div id="openMeetingOverrunModal" data-bs-toggle="modal" data-bs-target={`#${MODAL_ID}`} style={{ display: "none" }} />
      <div id={MODAL_ID} className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false" style={{ zIndex: 100030 }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px", boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)" }}>
            <div className="modal-header border-0 pb-1">
              <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Meeting Still in Progress?</h5>
              <div id="closeMeetingOverrunModal" data-bs-dismiss="modal" style={{ display: "none" }} />
            </div>
            <div className="modal-body pt-2 pb-2">
              <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "6px" }}>
                Your meeting <b>{asking ? asking.title : ""}</b> was set to end at <b>{end ? end.format("h:mm A") : ""}</b> ({overText}).
              </p>
              <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: 0 }}>
                Is it still in progress? If so, you will be asked again in 15 minutes.
              </p>
            </div>
            <div className="modal-footer border-0 pt-1">
              <button type="button" className="btn btn-light waves-effect" onClick={stillRunning} disabled={busy} style={{ borderRadius: "5px", fontSize: "13px" }}>Still in progress</button>
              <button type="button" className="btn btn-primary waves-effect waves-light" onClick={markComplete} disabled={busy} style={{ borderRadius: "5px", fontSize: "13px" }}>Mark it Complete</button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default MeetingOverrunPrompt;
